import { OptionsApi } from '@core/swagger-client';
import { Button, ErrorMessage, Heading, Loader } from '@core/ui';
import { useParams } from '@online-webinar/router';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { AuthPage, AuthPageContent } from '../..';

export const UnsubscribePage = () => {
    const { t } = useTranslation();
    const { token } = useParams({ strict: false });

    const unsubscribeMutation = useMutation(OptionsApi.unsubscribe.unsubscribe());
    const resubscribeMutation = useMutation(OptionsApi.unsubscribe.resubscribe());

    const [brandTitle, setBrandTitle] = useState<string>('');
    const [resubscribed, setResubscribed] = useState(false);

    const hasFired = useRef(false);
    useEffect(() => {
        if (hasFired.current) return;
        hasFired.current = true;
        unsubscribeMutation.mutate(token ?? '', {
            onSuccess: (result) => setBrandTitle(result.brandTitle)
        });
    }, []);

    const handleResubscribe = () => {
        resubscribeMutation.mutate(token ?? '', {
            onSuccess: (result) => {
                setBrandTitle(result.brandTitle);
                setResubscribed(true);
            }
        });
    };

    // isIdle is true before the effect's mutate() call has fired (SSR render, and the instant after
    // hydration before the effect runs) — without it, isPending/isError/resubscribed are all false
    // and the component falls through to the "success" branch before the request has even been sent.
    const isLoading = unsubscribeMutation.isPending || unsubscribeMutation.isIdle;

    return (
        <AuthPage>
            <AuthPageContent>
                <div data-testid="unsubscribe-page" className="flex flex-col gap-6">
                    {isLoading ? (
                        <Loader />
                    ) : unsubscribeMutation.isError ? (
                        <>
                            <Heading variant="h1">{t('Unsubscribe.Error.Title')}</Heading>
                            <ErrorMessage apiError={unsubscribeMutation.error} />
                        </>
                    ) : resubscribed ? (
                        <>
                            <Heading variant="h1">{t('Unsubscribe.Resubscribed.Title')}</Heading>
                            <p>
                                <Trans values={{ brand: brandTitle }} i18nKey="Unsubscribe.Resubscribed.Body" />
                            </p>
                        </>
                    ) : (
                        <>
                            <Heading variant="h1">{t('Unsubscribe.Success.Title')}</Heading>
                            <p data-testid="unsubscribe-success-message">
                                <Trans values={{ brand: brandTitle }} i18nKey="Unsubscribe.Success.Body" />
                            </p>
                            {resubscribeMutation.isError && <ErrorMessage apiError={resubscribeMutation.error} />}
                            <p>{t('Unsubscribe.Resubscribe.Prompt')}</p>
                            <Button
                                data-testid="unsubscribe-resubscribe"
                                variant="primary"
                                isLoading={resubscribeMutation.isPending}
                                onClick={handleResubscribe}
                            >
                                {t('Unsubscribe.Resubscribe.Button')}
                            </Button>
                        </>
                    )}
                </div>
            </AuthPageContent>
        </AuthPage>
    );
};
